<template>
  <div style="z-index: 999999999;">
    <div class="backdrop" />
    <div class="activation-wrapper flex center-all blur">
      <div
        v-if="permissionDenied && permissionDenied.message"
        class="activation-card custom-message">
        <h3>🚫 {{ permissionDenied.message }}</h3>
      </div>

      <div
        v-else-if="currentOrganisation && currentOrganisation.activate_items && currentOrganisation.activate_items.is_trial"
        class="activation-card start-free-trial" >
        <h3>Start your free trial</h3>
        <p>You're just one step away from activating your free trial.</p>
        <div class="divider dotted-border" />
        <h3 class="blue"><span class="emoji-fix">🥳</span> {{ currentOrganisation.activate_items.included_balance.amount }}$ included in your free trial</h3>
        <p class="black-light">Get started with free funds. <span class="underline">You won't be charged</span> until you've spent all the included trial funds.</p>
        <div class="divider dotted-border" />
        <p>When your free funds are spent, you'll automatically be signed up for the {{ currentOrganisation.activate_items.plan }} plan at ${{ currentOrganisation.activate_items.price.amount }} per month{{ currentOrganisation.activate_items.included_funds ? `, with a minimum monthly top-up of $${currentOrganisation.activate_items.included_funds}.` : '.' }}
          <span
            v-if="!currentOrganisation.activate_items.included_funds"
            class="underline" > You can cancel at any time.</span>
        </p>
        <div
          v-if="currentOrganisation.activate_items && currentOrganisation.activate_items.included_items"
          class="hover-text flex center-all inline"
          style="margin-top: 8px;"
          @click="planDetailsVisible = !planDetailsVisible" >
          <p class="p-0">View plan details</p>
          <SBIcon
            :icon="SBIconList.DOWN_ARROW"
            :color="SBColors.greyPrimary"
            :hover-size="18"
            :border-radius="50"
            animate
            :rotate="planDetailsVisible ? 0 : -90"
            :size="11"/>
        </div>
        <b-collapse
          v-model="planDetailsVisible"
          class="plan-details" >
          <div class="flex">
            <div
              v-if="currentOrganisation.activate_items && currentOrganisation.activate_items.included_items"
              style="width: 40%;">
              <h3>Included:</h3>
              <p
                v-for="(item, index) in currentOrganisation.activate_items.included_items.included_in_plan"
                :key="`included-${index}`" >
                <span
                  v-if="item.icon"
                  class="emoji-fix" >{{ item.icon }} </span>{{ item.quantity }} x {{ item.name }}
              </p>
            </div>
            <div
              v-if="currentOrganisation.activate_items && currentOrganisation.activate_items.included_items"
              style="width: 60%;">
              <h3>Overage & usage fees:</h3>
              <p
                v-for="(item, index) in currentOrganisation.activate_items.included_items.overage"
                :key="`overage-${index}`" >
                {{ item.name }} - ${{ item.price.amount }} / {{ currentOrganisation.activate_items.billing_cycle }}
              </p>
              <p
                v-for="(item, index) in currentOrganisation.activate_items.included_items.usage_fees"
                :key="`usage-${index}`" >
                {{ item.name }} - ${{ item.price.amount }} / {{ item.price.billing_cycle }}
              </p>
            </div>
          </div>
        </b-collapse>
        <div class="card-input" >
          <label class="card-label">Enter card details</label>
          <StripeCard
            ref="stripeCard"
            :public-key="$env.VUE_APP_STRIPE_PUBLIC"
            @handle-error="handleStripeErrors"
            @complete="complete = true"
            @incomplete="complete = false"
            @submit="confirm" />
        </div>
        <div
          class="footer flex justify-end" >
          <b-button
            class="pay-btn flex center-all activation-actions"
            variant="primary"
            :disabled="saving || !complete"
            :class="{loading: saving}"
            @click="confirm" >
            <span>Start free trial</span>
            <Spinner
              v-if="saving"
              :size="14"
              :thickness="2"
              :color="SBColors.white" />
          </b-button>
        </div>
      </div>

      <div
        v-else-if="activation && currentOrganisation && currentOrganisation.activate_items"
        class="activation-card" >
        <h3><span class="emoji-fix">🎉</span> Welcome to AI Number</h3>
        <p>You're just one step away from activating your account.</p>
        <div class="divider dotted-border" />
        <h4>{{ currentOrganisation.activate_items.plan }} Plan</h4>
        <p>
          <span class="emoji-fix">💰</span> {{ Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2, style: 'currency', currency: currentOrganisation.activate_items.price.currency }).format(currentOrganisation.activate_items.price.amount) }} / month
        </p>
        <p
          v-if="currentOrganisation.activate_items.free_trial"
          class="py-0" ><span class="emoji-fix">🥳</span> {{ currentOrganisation.activate_items.free_trial }}-day free trial</p>

        <div class="divider dotted-border" />
        <div class="flex plan-details p-0">
          <div
            v-if="currentOrganisation.activate_items && currentOrganisation.activate_items.included_items"
            style="width: 40%;">
            <h3>Included:</h3>
            <p
              v-for="(item, index) in currentOrganisation.activate_items.included_items.included_in_plan"
              :key="`included-${index}`" >
              <span
                v-if="item.icon"
                class="emoji-fix" >{{ item.icon }} </span>{{ item.quantity }} x {{ item.name }}
            </p>
          </div>
          <div
            v-if="currentOrganisation.activate_items && currentOrganisation.activate_items.included_items"
            style="width: 60%;">
            <h3>Overage & usage fees:</h3>
            <p
              v-for="(item, index) in currentOrganisation.activate_items.included_items.overage"
              :key="`overage-${index}`" >
              {{ item.name }} - ${{ item.price.amount }} / {{ currentOrganisation.activate_items.billing_cycle }}
            </p>
            <p
              v-for="(item, index) in currentOrganisation.activate_items.included_items.usage_fees"
              :key="`usage-${index}`" >
              {{ item.name }} - ${{ item.price.amount }} / {{ item.price.billing_cycle }}
            </p>
          </div>
        </div>
        <div class="card-input">
          <label class="card-label">Enter card details</label>
          <StripeCard
            ref="stripeCard"
            :public-key="$env.VUE_APP_STRIPE_PUBLIC"
            @handle-error="handleStripeErrors"
            @complete="complete = true"
            @incomplete="complete = false"
            @submit="confirm" />
          <div
            v-if="errorMessage"
            class="error error-color flex flex-align-center">
            <SBIcon
              class="mr-2"
              :icon="SBIconList.ERROR"
              :color="SBColors.redPrimary"
              :size="12" />
            <span>{{ errorMessage }}</span>
          </div>
        </div>
        <div class="flex justify-end">
          <b-button
            variant="primary"
            :disabled="saving || !complete"
            :class="{loading: saving}"
            class="flex activation-actions"
            @click="confirm" >
            <span>Activate your account</span>
            <Spinner
              v-if="saving"
              :size="14"
              :thickness="2"
              :color="SBColors.white" />
          </b-button>
        </div>
      </div>

      <div
        v-else
        class="activation-card"
        :class="{ success: !historyOverdue.length }" >
        <h3 v-if="historyOverdue.length"><span class="emoji-fix">⛔</span> Your account has overdue invoices</h3>
        <p v-if="historyOverdue.length">Pay overdue invoices to continue using AI Number.</p>
        <span
          v-if="!historyOverdue.length"
          class="flex column center-all">
          <span style="font-size: 26px;">🎉</span>
          <h2 style="font-weight: 500; padding: 8px 0 3px;">Overdue invoices were successfully paid</h2>
          <p>Your AI Number account will be unblocked shortly.</p>
        </span>
        <template v-if="isOwner || isAdmin" >
          <div
            v-for="(historyInvoice, index) in historyOverdue"
            :key="index"
            class="history flex center-all border-top"
            :class="[historyInvoice.status]" >
            <SBIcon
              v-if="historyInvoice.status === 'open'"
              v-b-popover.hover="{ content: 'Open', animation: false, customClass: 'basic-popover', placement: 'top' }"
              class="mr-2"
              :icon="SBIconList.ARROW_ROUNDED"
              color="#697FFF"
              background-color="#e0e4ff"
              :hover-size="18"
              :border-radius="50"
              :rotate="135"
              :size="10"/>
            <SBIcon
              v-else-if="historyInvoice.status === 'failed'"
              v-b-popover.hover="{ content: 'Overdue', animation: false, customClass: 'basic-popover', placement: 'top' }"
              class="mr-2"
              :icon="SBIconList.CLOSE_THICK"
              color="#ec5a61"
              background-color="#ffd8d9"
              :hover-size="18"
              :border-radius="50"
              :size="6"/>
            <p>{{ (historyInvoice.created_at * 1000) | dateMMMDDYYYY }} - {{ Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(historyInvoice.total) }}</p>
            <span class="greedy dashes" />

            <a
              v-if="['failed', 'open'].includes(historyInvoice.status)"
              :href="historyInvoice.hosted_invoice_url"
              target="_blank" >
              <b-button class="pay-btn flex center-all" >
                <span>Pay</span>
              </b-button>
            </a>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import StripeCard from '@/components/common/StripeCard';
import Spinner from '@/components/common/Spinner';
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import UserPermissionsMixin from '@/mixins/UserPermissions.mixin';

export default {
  name: 'Activation',
  components: {
    StripeCard,
    Spinner,
    SBIcon,
  },
  mixins: [
    UserPermissionsMixin,
  ],
  props: {
    activation: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data() {
    return {
      complete: false,
      errorMessage: '',
      saving: false,
      history: null,
      planDetailsVisible: false,
      SBColors,
      SBIconList,
    };
  },
  computed: {
    ...mapGetters({
      currentOrganisation: 'organisation/currentOrganisation',
      permissionDenied: 'user/permissionDenied',
    }),
    historyOverdue() {
      if (this.currentOrganisation && this.currentOrganisation.should_reactivate_account) {
        return this.currentOrganisation && this.currentOrganisation.activate_items ? this.currentOrganisation.activate_items.filter(i => ['failed', 'open'].includes(i.status)) : [];
      }
      return [];
    },
  },
  watch: {
    historyOverdue(newValue, oldValue) {
      if (!newValue.length && newValue.length !== oldValue.length) {
        window.location.reload();
      }
    },
  },
  methods: {
    ...mapActions({
      changePaymentCard: 'user/changePaymentCard',
    }),
    handleStripeErrors(error = '') {
      this.errorMessage = error;
      this.saving = false;
    },
    confirm() {
      if (this.saving || !this.complete) {
        return;
      }
      this.saving = true;
      this.errorMessage = '';
      this.$refs.stripeCard.getStripeToken(this.changeCard);
    },
    changeCard() { // TODO this fails at the moment because activating subscription is an issue.
      this.changePaymentCard({
        data: {
          card_token: this.$refs.stripeCard.token,
          activate_subscription: true,
          includeMe: true,
          forceApi: true,
        },
        organisationId: this.currentOrganisation.id,
      })
        .then((response) => {
          if (response.status === 200) {
            localStorage.setItem('forceSplashMessage', '🎉 Your subscription was successfully activated. We are getting everything ready...');
            window.location.reload();
          }
        })
        .catch((error) => {
          this.errorMessage = error.response.data.message;
          this.saving = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.error {
  font-size: 14px;
}

.backdrop {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(42, 56, 63, 0.27);
  backdrop-filter: blur(50px);
  opacity: 0.97;
}

.activation-wrapper {
  // backdrop-filter: blur(8px);
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;

  .activation-card {
    background: $white;
    width: 90%;
    max-width: 484px;
    padding: 20px 18px;
    border-radius: 5px;
    box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.08);

    &.success, &.custom-message {
      width: unset;
    }

    h3 {
      font-size: 16px;
      font-weight: 500;
      line-height: 1.3;
      padding: 3px 0;
    }

    p {
      font-size: 14px;
      line-height: 1.3;
      padding: 2px 0 3px 0;
      color: $blackUltralight;
    }

    .dotted-border {
      background-image: linear-gradient(to left, $greyBorder 50%, rgba(255,255,255,0) 0%);
      background-position: bottom;
      height: 1px;
      background-size: 8px 1px;
      background-repeat: repeat-x;
      margin: 15px 0;
    }

    h4 {
      font-size: 14px;
      font-weight: 500;
      line-height: 1.37;
      color: #54626c;
      margin-bottom: 2px;
    }

    .card-input {
      margin: 20px -18px;
      padding: 18px 18px 20px;
      background-color: $greyUltralight;
      border: solid 1px $greyBorderModal;
      border-left: none;
      border-right: none;

      .card-label {
        color: $blackPrimary;
        font-size: 14px;
        font-weight: 500;
        margin-top: 0px;
      }
    }

    .activation-actions {
      position: relative;

      &.loading span {
        visibility: hidden;
      }

      .loader {
        position: absolute;
        height: 100%;
        display: flex;
        align-items: center;
      }
    }

    .history {
      &:first-of-type {
        margin-top: 20px;
      }

      margin: 0 -18px;
      padding: 12px 18px;

      p {
        font-size: 14px;
        font-weight: normal;
        line-height: 1.2;
        color: $blackPrimary;
      }

      button {
        padding: 4px 12px !important;
      }

      &:last-of-type {
        margin-bottom: -20px;
      }
    }

    .emoji-fix {
      font-size: 14px !important;
      top: -1px;
      position: relative;
    }

    .hover-text {
      cursor: pointer;
      p {
        font-size: 14px;
        font-weight: 500;
        line-height: 1.2;
        color: $blackUltralight;
      }
      &:hover {
        p {
          color: $blackPrimary;
        }
        ::v-deep .sb-icon .mask {
          background-color: $blackPrimary !important;
        }
      }
    }

    .plan-details {
      padding: 14px 0 0;

      h3 {
        font-size: 14px;
        line-height: 1.37;
        color: #54626c;
        margin-bottom: 4px;
      }

      p {
        font-size: 14px;
        line-height: 1.2;
        color: $blackUltralight;

      }

      .emoji-fix {
        font-size: 12px !important;
        top: -2px;
        position: relative;
      }
    }

    .footer {
      margin-top: 20px;
    }
  }
}
</style>
